// Expandable application card with inline editing
// Collapsed: status dot + name + close date + chevron + progress bar below
// Expanded: replaces collapsed view with editable fields

import { useState, useEffect } from 'react';
import {
  ApplicationStatus,
  type Application,
  type UpdateApplicationPayload,
} from '@startus/applications';
import { InlineField } from './InlineField';
import { CountrySelect } from './CountrySelect';
import { StatusSelect } from './StatusSelect';
import { ProgressBar } from '@startus/ui';
import { isValidWebUrl } from '@startus/lib';
import { STATUS_PROGRESS } from './statusProgress';

// Color mapping for status dot indicator
const STATUS_COLORS: Record<ApplicationStatus, string> = {
  [ApplicationStatus.NOT_STARTED]: '#9ca3af',
  [ApplicationStatus.IN_PROGRESS]: '#facc15',
  [ApplicationStatus.SENT]: '#60a5fa', // blue-400
  [ApplicationStatus.WAITING_RESPONSE]: '#22c55e', // green-500
  [ApplicationStatus.NOT_SELECTED]: '#ef4444',
};

// Labels passed from page (i18n)
// edit-only fields are optional so view-mode callers can omit them
export interface ApplicationCardLabels {
  statusLabels: Record<ApplicationStatus, string>;
  closeDate: string;
  country: string;
  url: string;
  startDate: string;
  endDate: string;
  progress: string;
  editedByPlaceholder: string;
  // edit-only
  clickToEdit?: string;
  newApplication?: string;
  save?: string;
  urlError?: string;
  nameError?: string;
  saveError?: string;
  // if provided, called with the caught error to get a translated message (overrides saveError)
  getSaveError?: (err: unknown) => string;
}

// Country option — value is ISO2 code, label is display name
export interface CountryOption {
  value: string;
  label: string;
}

// Resolved editor info — passed from page after UUID → UserInfo lookup
export interface EditorInfo {
  name: string;
  avatarUrl: string | null;
}

interface ApplicationCardProps {
  application: Application;
  expanded: boolean;
  onToggle: () => void;
  mode?: 'edit' | 'view';
  onSave?: (id: string, updates: UpdateApplicationPayload) => Promise<void>;
  onDelete?: (id: string) => void;
  labels: ApplicationCardLabels;
  countryOptions?: CountryOption[];
  editorInfo?: EditorInfo; // resolved from lastEditedBy UUID
  loading?: boolean;
  isLast?: boolean; // suppresses the bottom inset divider on the last card
}

// Format YYYY-MM-DD or ISO string to DD.MM.YYYY for display
// Parses date parts directly to avoid timezone shift
function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const [datePart] = dateStr.split('T');
  const [year, month, day] = datePart.split('-');
  if (!year || !month || !day) return '';
  return `${day}.${month}.${year}`;
}

// Extract YYYY-MM-DD from ISO string for date input
function toInputDate(iso: string): string {
  if (!iso) return '';
  return iso.split('T')[0];
}

// Convert YYYY-MM-DD to ISO for API — uses noon to avoid timezone day-shift
function toISODate(dateStr: string): string {
  if (!dateStr) return '';
  return `${dateStr}T12:00:00.000Z`;
}

export function ApplicationCard({
  application,
  expanded,
  onToggle,
  mode = 'edit',
  onSave,
  onDelete,
  labels,
  countryOptions = [],
  editorInfo,
  loading = false,
  isLast = false,
}: ApplicationCardProps) {
  const [urlError, setUrlError] = useState('');
  const [nameError, setNameError] = useState('');
  const [saveError, setSaveError] = useState('');

  // Local form state - only persists on save
  const [form, setForm] = useState({
    name: application.name,
    url: application.url,
    country: application.country || '',
    startDate: toInputDate(application.startDate),
    endDate: toInputDate(application.endDate),
    status: application.status,
    progress: application.progress ?? STATUS_PROGRESS[application.status],
  });

  // Reset form when application prop changes (e.g. after save + refetch)
  useEffect(() => {
    setForm({
      name: application.name,
      url: application.url,
      country: application.country || '',
      startDate: toInputDate(application.startDate),
      endDate: toInputDate(application.endDate),
      status: application.status,
      progress: application.progress ?? STATUS_PROGRESS[application.status],
    });
  }, [application]);

  // Update progress default when status changes
  const handleStatusChange = (newStatus: ApplicationStatus) => {
    setForm((prev) => ({
      ...prev,
      status: newStatus,
      progress: STATUS_PROGRESS[newStatus],
    }));
  };

  const handleSave = async () => {
    // Client-side validation before API call
    let hasError = false;
    if (!form.name.trim()) {
      setNameError(labels.nameError ?? '');
      hasError = true;
    } else {
      setNameError('');
    }
    if (!isValidWebUrl(form.url)) {
      setUrlError(labels.urlError ?? '');
      hasError = true;
    } else {
      setUrlError('');
    }
    if (hasError) return;

    setSaveError('');
    try {
      await onSave?.(application.id, {
        name: form.name,
        url: form.url,
        country: form.country || undefined,
        startDate: toISODate(form.startDate),
        endDate: toISODate(form.endDate),
        status: form.status,
        progress: form.progress,
      });
    } catch (err) {
      // Keep panel open and show error — use getSaveError if provided for code-specific messages
      setSaveError(labels.getSaveError ? labels.getSaveError(err) : (labels.saveError ?? ''));
    }
  };

  const statusColor = STATUS_COLORS[form.status] || '#9ca3af';

  // Expanded view — replaces collapsed entirely
  if (expanded) {
    // View-only panel: same layout as edit but no inputs or action buttons
    if (mode === 'view') {
      return (
        <div>
          <div className="px-0 md:px-4 py-3">
            {/* Top row: status label + collapse chevron (no dropdown, no delete) */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span
                  className="w-4 h-4 rounded-full pr-4"
                  style={{ backgroundColor: statusColor }}
                />
                <span className="text-sm">{labels.statusLabels[form.status]}</span>
              </div>
              <button
                type="button"
                onClick={onToggle}
                className="btn btn-ghost btn-sm"
                aria-label="Collapse"
              >
                <svg
                  className="w-4 h-4 text-base-content/40 rotate-180"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>

            {/* Name */}
            <p className="font-normal text-xl mb-2 px-1">{form.name}</p>

            {/* URL — a link only when it's a valid http(s) URL (same check as edit mode);
                any other stored value is shown as plain text */}
            <div className="flex items-center gap-2 px-1 py-0.5 mb-0.5">
              <svg
                className="w-5 h-5 text-[#EAEAEA] shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 16 16"
              >
                <path
                  d="M9.33333 7.99777C9.33333 6.33662 7.78869 4.66602 5.90476 4.66602C5.6816 4.66602 4.94602 4.66602 4.7619 4.66602C2.86835 4.66602 1.33333 6.15769 1.33333 7.99777C1.33333 9.58337 2.47315 10.9103 3.99999 11.247C4.24503 11.301 4.50004 11.3295 4.7619 11.3295"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6.66667 7.99833C6.66667 9.65948 8.21131 11.3301 10.0952 11.3301C10.3184 11.3301 11.054 11.3301 11.2381 11.3301C13.1316 11.3301 14.6667 9.8384 14.6667 7.99833C14.6667 6.41272 13.5269 5.08581 12 4.74914C11.755 4.69511 11.5 4.66658 11.2381 4.66658"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {isValidWebUrl(form.url) ? (
                <a
                  href={form.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#19A2F8] underline underline-offset-6 truncate"
                >
                  {form.url}
                </a>
              ) : form.url ? (
                <span className="text-sm text-[#EAEAEA] truncate">{form.url}</span>
              ) : (
                <span className="text-sm text-[#EAEAEA]/40">{labels.url}</span>
              )}
            </div>

            {/* Country */}
            <div className="flex items-center justify-between gap-2 px-1 py-0.5">
              <span className="text-sm text-[#EAEAEA] min-w-30 shrink-0">{labels.country}</span>
              <span className="text-sm text-[#19A2F8]">{form.country || '—'}</span>
            </div>

            {/* Start date */}
            <div className="flex items-center justify-between gap-2 px-1 py-0.5">
              <span className="text-sm text-[#EAEAEA] min-w-30 shrink-0">{labels.startDate}</span>
              <span className="text-sm text-[#19A2F8]">
                {form.startDate ? formatDate(toISODate(form.startDate)) : '—'}
              </span>
            </div>

            {/* End date */}
            <div className="flex items-center justify-between gap-2 px-1 py-0.5">
              <span className="text-sm text-[#EAEAEA] min-w-30 shrink-0">{labels.endDate}</span>
              <span className="text-sm text-[#19A2F8]">
                {form.endDate ? formatDate(toISODate(form.endDate)) : '—'}
              </span>
            </div>

            {/* Progress — read-only bar */}
            <div className="mt-1">
              <div className="flex justify-between items-center mb-2 px-1">
                <p className="text-sm text-gray-400">{labels.progress}</p>
                <p className="text-sm text-gray-400">{form.progress}%</p>
              </div>
              <ProgressBar value={form.progress} size="sm" color={statusColor} />
            </div>

            {/* Last edited info */}
            {(editorInfo || application.lastEditedAt) && (
              <div className="flex items-center gap-2 text-xs text-base-content/40 pt-2 px-1">
                {editorInfo &&
                  (editorInfo.avatarUrl ? (
                    <img
                      src={editorInfo.avatarUrl}
                      className="w-5 h-5 rounded-full border border-white object-cover"
                      alt=""
                    />
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-primary/20 border border-white flex items-center justify-center text-[10px] font-medium text-primary uppercase">
                      {editorInfo.name.charAt(0)}
                    </div>
                  ))}
                <span>
                  {editorInfo && `${labels.editedByPlaceholder} ${editorInfo.name}`}
                  {editorInfo && application.lastEditedAt && ' · '}
                  {application.lastEditedAt && formatDate(application.lastEditedAt)}
                </span>
              </div>
            )}
          </div>
          {!isLast && <div className="mx-4 border-b border-white/10" />}
        </div>
      );
    }

    return (
      <div>
        <div className="px-0 md:px-4 py-3">
          {/* Top row: status dropdown + delete + collapse chevron */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span
                className="w-4 h-4 rounded-full pr-4"
                style={{ backgroundColor: statusColor }}
              />
              <StatusSelect
                value={form.status}
                onChange={handleStatusChange}
                statusLabels={labels.statusLabels}
                statusColors={STATUS_COLORS}
              />
            </div>

            <div className="flex items-center gap-1">
              {/* Delete button */}
              <button
                type="button"
                onClick={() => onDelete?.(application.id)}
                className="btn btn-ghost btn-sm"
                aria-label="Delete"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M13.3333 6L12.0033 13.5642C11.8912 14.2017 11.3374 14.6667 10.6902 14.6667H5.30979C4.66249 14.6667 4.10869 14.2017 3.9966 13.5642L2.66663 6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M14 3.9987H10.25M2 3.9987H5.75M5.75 3.9987V2.66536C5.75 1.92898 6.34695 1.33203 7.08333 1.33203H8.91667C9.65307 1.33203 10.25 1.92898 10.25 2.66536V3.9987M5.75 3.9987H10.25"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {/* Collapse chevron */}
              <button
                type="button"
                onClick={onToggle}
                className="btn btn-ghost btn-sm"
                aria-label="Collapse"
              >
                <svg
                  className="w-4 h-4 text-base-content/40 rotate-180"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Name field — no label, bold; placeholder doubles as click-to-edit hint in display mode */}
          <InlineField
            value={form.name}
            placeholder={labels.clickToEdit}
            onChange={(v) => {
              setNameError('');
              setForm((prev) => ({ ...prev, name: v }));
            }}
            className="font-normal mb-2"
            textSize="text-xl md:text-xl"
          />
          {nameError && <p className="text-xs text-error px-1">{nameError}</p>}

          {/* URL field */}
          <InlineField
            value={form.url}
            placeholder={labels.clickToEdit}
            onChange={(v) => {
              setUrlError('');
              setForm((prev) => ({ ...prev, url: v }));
            }}
            type="url"
            textSize="text-sm md:text-base"
          />
          {urlError && <p className="text-xs text-error px-1">{urlError}</p>}

          {/* Country select — label-left / value-right layout matching InlineField */}
          <div className="flex items-center justify-between gap-2 px-1 py-0.5">
            <span className="text-sm md:text-base text-[#EAEAEA] min-w-30 shrink-0">
              {labels.country}
            </span>
            <CountrySelect
              value={form.country}
              onChange={(iso) => setForm((prev) => ({ ...prev, country: iso }))}
              options={countryOptions}
              placeholder={labels.clickToEdit}
            />
          </div>

          {/* Start date — can't be after end date */}
          <InlineField
            value={form.startDate}
            displayValue={form.startDate ? formatDate(toISODate(form.startDate)) : ''}
            placeholder={labels.clickToEdit}
            onChange={(v) => setForm((prev) => ({ ...prev, startDate: v }))}
            type="date"
            label={labels.startDate}
            max={form.endDate}
            textSize="text-sm md:text-base"
          />

          {/* End date — can't be before start date */}
          <InlineField
            value={form.endDate}
            displayValue={form.endDate ? formatDate(toISODate(form.endDate)) : ''}
            placeholder={labels.clickToEdit}
            onChange={(v) => setForm((prev) => ({ ...prev, endDate: v }))}
            type="date"
            label={labels.endDate}
            min={form.startDate}
            textSize="text-sm md:text-base"
          />

          <div>
            <div className="flex justify-between items-center mb-2 px-1">
              <p className="text-sm md:text-base text-gray-400">{labels.progress}</p>
              <p className="text-sm md:text-base text-gray-400">{form.progress}%</p>
            </div>

            <ProgressBar
              value={form.progress}
              onChange={(v) => setForm((prev) => ({ ...prev, progress: v }))}
              editable
            />
          </div>

          {/* Last edited by — only shown when editor or date is available */}
          {(editorInfo || application.lastEditedAt) && (
            <div className="flex items-center gap-2 text-xs text-base-content/40 pt-2 px-1">
              {editorInfo &&
                (editorInfo.avatarUrl ? (
                  <img
                    src={editorInfo.avatarUrl}
                    className="w-5 h-5 rounded-full border border-white object-cover"
                    alt=""
                  />
                ) : (
                  // Fallback initial avatar
                  <div className="w-5 h-5 rounded-full bg-primary/20 border border-white flex items-center justify-center text-[10px] font-medium text-primary uppercase">
                    {editorInfo.name.charAt(0)}
                  </div>
                ))}
              <span>
                {editorInfo && `${labels.editedByPlaceholder} ${editorInfo.name}`}
                {editorInfo && application.lastEditedAt && ' · '}
                {application.lastEditedAt && formatDate(application.lastEditedAt)}
              </span>
            </div>
          )}

          {/* Save button + API error */}
          <div className="flex items-center justify-between pt-2">
            {saveError ? <p className="text-xs text-error">{saveError}</p> : <span />}
            <button
              type="button"
              onClick={handleSave}
              disabled={loading}
              className="inline-flex items-center gap-1 bg-primary hover:bg-primary/90 active:bg-primary/80 text-primary-content text-sm font-normal px-3 py-1.5 rounded-2xl focus:outline-none select-none transition-colors disabled:opacity-50"
            >
              {loading && <span className="loading loading-spinner loading-xs" />}
              {labels.save}
            </button>
          </div>
        </div>
        {!isLast && <div className="mx-4 border-b border-white/10" />}
      </div>
    );
  }

  // Collapsed view — status dot + name + close date + chevron + progress bar below
  return (
    <div className="hover:bg-white/5 rounded-lg">
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer rounded-lg"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onToggle()}
      >
        {/* Status dot */}
        <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: statusColor }} />

        {/* Name — updates live from form state */}
        <span className="text-sm md:text-base font-medium flex-1 truncate">
          {form.name || labels.newApplication}
        </span>

        {/* Close date */}
        <span className="text-xs md:text-sm text-base-content/60 shrink-0">
          {labels.closeDate} {formatDate(form.endDate || application.endDate)}
        </span>

        {/* Chevron */}
        <svg
          className="w-4 h-4 text-base-content/40 shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {/* Thin progress bar indicator */}
      <div className="px-4 pb-2">
        <ProgressBar value={form.progress} size="sm" color={statusColor} />
      </div>
      {!isLast && <div className="mx-4 border-b border-white/10" />}
    </div>
  );
}
